// <s-reel> is the vertical episode feed: it plays the episode in view, pauses the rest, and jumps to an episode on --goto.
class SReel extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    const feed = this.querySelector('[data-reel-feed]');
    const slides = [...this.querySelectorAll('[data-episode]')];
    if (!feed || !slides.length) return;
    this.#readResume();
    this.#setupPip();
    this.#setupEpisodeKeys();
    this.#setupSwipe(feed);
    this.#watchEmbeds();
    this.#pollPlayers();
    this.#setupTapThrough();
    this.#setupIdle();
    // Whichever video starts (an episode, the mini player, a quality switch) becomes the only one playing.
    document.addEventListener('play', (event) => {
      if (event.target instanceof HTMLMediaElement) this.#stopOthers(event.target);
    }, { capture: true, signal: this.#ac.signal });

    this.#startAtSaved(feed);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const video = entry.target.querySelector('video');
        const embed = entry.target.querySelector('iframe[data-yt], iframe[data-vimeo]');
        const cloudflare = entry.target.querySelector('iframe[data-cloudflare]');
        // Linked videos load as soon as their episode starts coming into view.
        if (entry.isIntersecting) this.#loadEmbed(entry.target);
        if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
          this.#setCurrent(entry.target.dataset.episode);
          // Get the next episode ready too, so switching is instant.
          const next = entry.target.nextElementSibling;
          if (next?.matches('[data-episode]')) this.#loadEmbed(next);
          // TikTok style: start downloading the next uploaded episode in the background (only if it's unlocked),
          // so the swipe plays instantly instead of waiting to load.
          if (video) {
            video.preload = 'auto';
            video.closest('s-stream')?.start?.();
          }
          const nextVideo = next?.matches('[data-episode]:not([data-locked])') ? next.querySelector('video') : null;
          const nextStream = nextVideo?.closest('s-stream');
          if (nextStream?.start) {
            // Adaptive stream: start it early at a light quality so the swipe plays instantly.
            nextVideo.preload = 'auto';
            nextStream.start();
          } else if (nextVideo && nextVideo.preload !== 'auto') {
            nextVideo.preload = 'auto';
            if (nextVideo.readyState < 3) nextVideo.load();
          }
          // While the scrollable pop-out window is open, it does the playing; the page stays quiet.
          if (entry.target.hasAttribute('data-locked') || this.#pipWindow) continue;
          if (video) this.#play(video);
          if (embed) this.#playEmbed(embed);
          if (cloudflare) this.#playCloudflare(cloudflare);
        } else {
          // Never pause the video showing in the pop-out window just because the page scrolled.
          if (video && video !== document.pictureInPictureElement) video.pause();
          if (embed) this.#send(embed, 'pause');
          if (cloudflare) this.#pauseCloudflare(cloudflare);
        }
      }
    }, { root: feed, threshold: [0, 0.6, 1] });
    slides.forEach((slide) => this.#observer.observe(slide));

    // TikTok style: one tap pauses (and shows every icon and the controls); tap again to play,
    // and the icons fade away 3 seconds later so only the movie shows.
    this.addEventListener('click', (event) => {
      const tap = event.target.closest('[data-yt-tap]');
      if (!tap) return;
      this.#togglePlayAt(tap);
      this.#wake();
    }, { signal: this.#ac.signal });
    // Uploaded episodes: keep the website's control bar in step with the video.
    for (const type of ['play', 'pause', 'timeupdate', 'volumechange', 'loadedmetadata', 'durationchange', 'ended', 'seeked']) {
      this.addEventListener(type, (event) => {
        if (event.target instanceof HTMLVideoElement) this.#syncBar(event.target);
      }, { capture: true, signal: this.#ac.signal });
    }
    // Linked videos only report back after a handshake. Start it now (the player may have loaded before this script)
    // and again on every load, and keep asking until the player answers.
    this.querySelectorAll('iframe[data-yt], iframe[data-vimeo]').forEach((embed) => {
      this.#handshake(embed);
      embed.addEventListener('load', () => {
        delete embed.dataset.ready;
        this.#handshake(embed);
        if (embed === this.#currentEmbed) this.#playEmbed(embed);
      }, { signal: this.#ac.signal });
    });
    window.addEventListener('message', (event) => this.#onEmbedMessage(event), { signal: this.#ac.signal });
    // Dragging the progress bar jumps the linked episode to that point.
    this.addEventListener('change', (event) => {
      const seek = event.target.closest?.('[data-embed-seek]');
      const slide = seek?.closest('[data-episode]');
      const video = slide?.querySelector('video');
      if (video) {
        video.currentTime = Number(seek.value) || 0;
        return;
      }
      const embed = slide?.querySelector('iframe[data-yt], iframe[data-vimeo]');
      if (embed) this.#seekTo(embed, Number(seek.value) || 0);
    }, { signal: this.#ac.signal });
    // Volume slider (0 = silent).
    this.addEventListener('input', (event) => {
      const input = event.target.closest?.('[data-embed-volume]');
      if (!input) return;
      const volume = Number(input.value);
      input.style.setProperty('--fill', `${volume * 100}%`);
      const slide = input.closest('[data-episode]');
      const video = slide?.querySelector('video');
      if (video) {
        delete video.dataset.autoMuted;
        video.volume = volume;
        video.muted = volume === 0;
        this.#syncPill();
        return;
      }
      const embed = slide?.querySelector('iframe[data-yt], iframe[data-vimeo]');
      if (!embed) return;
      embed.dataset.volume = String(volume);
      if (volume === 0) {
        this.#send(embed, 'mute');
        return;
      }
      if (embed.dataset.muted !== 'false' && this.#canUnmute(embed)) this.#send(embed, 'unmute');
      const ready = this.#playerReady.get(embed);
      if (ready?.type === 'yt') ready.player.setVolume?.(Math.round(volume * 100));
      else if (ready?.type === 'vimeo') ready.player.setVolume(volume).catch(() => {});
    }, { signal: this.#ac.signal });
    this.addEventListener('input', (event) => {
      const seek = event.target.closest?.('[data-embed-seek]');
      const time = seek?.closest('[data-episode]')?.querySelector('[data-embed-time]');
      if (!time) return;
      time.textContent = `${String(Math.floor(seek.value / 60)).padStart(2, '0')}:${String(Math.floor(seek.value % 60)).padStart(2, '0')}`;
      const max = Number(seek.max) || 0;
      seek.style.setProperty('--fill', `${max > 0 ? (Number(seek.value) / max) * 100 : 0}%`);
    }, { signal: this.#ac.signal });
    // Full screen: the whole episode where the browser allows it; uploaded videos on iPhone use the phone's own full screen.
    this.querySelectorAll('[data-embed-fullscreen]').forEach((button) => {
      const video = button.closest('[data-episode]')?.querySelector('video');
      button.hidden = !(document.fullscreenEnabled || video?.webkitEnterFullscreen);
    });

    // Any first touch, tap, or key press counts as permission to turn sound on.
    for (const type of ['pointerup', 'touchend', 'click', 'keydown']) {
      document.addEventListener(type, (event) => this.#unmute(event), { signal: this.#ac.signal, passive: true });
    }
    this.addEventListener('volumechange', (event) => {
      if (event.target === this.#current && !event.target.muted) {
        delete event.target.dataset.autoMuted;
        this.#syncPill();
      }
    }, { capture: true, signal: this.#ac.signal });

    // Auto-play next: an ended episode starts a short countdown, then scrolls to the next episode.
    this.addEventListener('ended', (event) => {
      if (event.target === this.#current) this.#startUpNext();
    }, { capture: true, signal: this.#ac.signal });

    this.addEventListener('command', (event) => {
      // Linked-episode control bar.
      if (event.command.startsWith('--embed-')) {
        const slide = event.source?.closest('[data-episode]');
        const video = slide?.querySelector('video');
        // Speed: 1.0x → 1.25x → 1.5x → 2x → 0.5x → 0.75x → back to 1.0x.
        if (event.command === '--embed-speed') {
          const speeds = [1, 1.25, 1.5, 2, 0.5, 0.75];
          const target = video || slide?.querySelector('iframe[data-yt], iframe[data-vimeo]');
          if (!target) return;
          const now = video ? video.playbackRate : Number(target.dataset.rate || 1);
          const next = speeds[(speeds.indexOf(now) + 1) % speeds.length] || 1;
          if (video) {
            video.playbackRate = next;
          } else {
            target.dataset.rate = String(next);
            const ready = this.#playerReady.get(target);
            if (ready?.type === 'yt') ready.player.setPlaybackRate?.(next);
            else if (ready?.type === 'vimeo') ready.player.setPlaybackRate(next).catch(() => {});
            else if (target.hasAttribute('data-yt')) target.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'setPlaybackRate', args: [next] }), '*');
            else target.contentWindow?.postMessage(JSON.stringify({ method: 'setPlaybackRate', value: next }), '*');
          }
          this.#syncBar(target);
          return;
        }
        if (video) {
          if (event.command === '--embed-play') this.#toggleVideo(video);
          if (event.command === '--embed-mute') {
            delete video.dataset.autoMuted;
            video.muted = !video.muted;
            if (!video.muted) video.volume = 1;
            this.#syncPill();
          }
          if (event.command === '--embed-fullscreen') {
            if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
            else if (document.fullscreenEnabled) slide.requestFullscreen().catch(() => {});
            else video.webkitEnterFullscreen?.();
          }
          return;
        }
        const embed = slide?.querySelector('iframe[data-yt], iframe[data-vimeo]');
        if (!embed) return;
        if (event.command === '--embed-play') this.#toggleEmbed(embed);
        if (event.command === '--embed-mute') this.#send(embed, embed.dataset.muted === 'false' ? 'mute' : 'unmute');
        if (event.command === '--embed-fullscreen') {
          if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
          else slide.requestFullscreen?.().catch(() => {});
        }
        return;
      }
      if (event.command === '--pip') {
        this.#togglePip();
        return;
      }
      if (event.command === '--minimize') {
        this.#minimize(event.source);
        return;
      }
      if (event.command === '--next') {
        this.#goNext();
        return;
      }
      if (event.command === '--cancel-next') {
        this.#stopUpNext();
        return;
      }
      if (event.command !== '--goto') return;
      const target = this.querySelector(`[data-episode="${event.source?.dataset.episode}"]`);
      if (!target) return;
      event.source.closest('dialog')?.close();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.#scrollToSlide(target, reduce);
    }, { signal: this.#ac.signal });
  }

  // Sound: try to play with sound; phones block that until the viewer touches the page, so a blocked episode plays
  // muted ("auto-muted") and the first tap anywhere, or the "Tap for sound" pill, turns sound on.
  // A viewer who mutes with the video controls stays muted (only auto-muted videos are unmuted).
  #current = null;

  // Only one video plays at a time: pauses every other video on the page and every other linked episode.
  #stopOthers(keep) {
    for (const media of document.querySelectorAll('video, audio')) {
      if (media !== keep && !media.paused) media.pause();
    }
    const frames = [...this.querySelectorAll('iframe[data-yt], iframe[data-vimeo]')];
    for (const embed of frames) {
      if (embed !== keep && embed.dataset.ready === 'true' && embed.dataset.playing !== 'false') this.#send(embed, 'pause');
    }
    for (const frame of this.querySelectorAll('iframe[data-cloudflare]')) {
      if (frame !== keep) this.#pauseCloudflare(frame);
    }
  }

  // Cloudflare Stream episodes: only the episode on screen is loaded. Coming into view loads it with autoplay
  // (with sound once the viewer has touched the page, otherwise muted as browsers require); leaving unloads it,
  // so it can never keep playing in the background.
  #cfCurrent = null;

  #playCloudflare(frame) {
    this.#cfCurrent = frame;
    this.#current = null;
    this.#currentEmbed = null;
    this.#stopOthers(frame);
    const base = frame.dataset.cfSrc;
    if (!base) return;
    // Asking for sound when the browser won't allow it makes Cloudflare's player stay stopped. Safari (iPhone, iPad, Mac)
    // never allows it inside the player, so there it always starts muted (viewers turn sound on with the player's speaker).
    const safari = this.#ios || (/Safari/.test(navigator.userAgent) && !/Chrome|Chromium|CriOS|Edg|Android/.test(navigator.userAgent));
    const sound = !safari && navigator.userActivation?.hasBeenActive;
    // Already loaded and playing (episode 1 on page load, or a muted start): leave it alone.
    if (frame.dataset.cfState === 'playing' || (frame.getAttribute('src') || '').startsWith(base)) {
      frame.dataset.cfState = 'playing';
      this.#watchCloudflare(frame);
      return;
    }
    frame.src = `${base}?autoplay=true&preload=auto${sound ? '' : '&muted=true'}`;
    frame.dataset.cfState = 'playing';
    this.#watchCloudflare(frame);
  }

  // Cloudflare's small player library tells the page when an episode plays, pauses or ends, so the
  // "Up next" countdown and the icon auto-hide work like they do for uploaded videos.
  static #cfLibrary = null;

  #loadCfLibrary() {
    if (window.Stream) return Promise.resolve();
    if (!SReel.#cfLibrary) {
      SReel.#cfLibrary = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://embed.cloudflarestream.com/embed/sdk.latest.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.append(script);
      });
    }
    return SReel.#cfLibrary;
  }

  #watchCloudflare(frame) {
    if (frame.dataset.cfWatch === 'true') return;
    frame.dataset.cfWatch = 'true';
    // Each new episode address loads a fresh player inside the frame; listen to it once it's ready.
    const attach = () => {
      const src = frame.getAttribute('src') || '';
      if (!src || src === 'about:blank' || frame.dataset.cfAttached === src) return;
      frame.dataset.cfAttached = src;
      this.#loadCfLibrary().then(() => {
        if (typeof window.Stream !== 'function' || frame.getAttribute('src') !== src) return;
        const player = window.Stream(frame);
        player.addEventListener('playing', () => {
          if (frame.getAttribute('src') === src) frame.dataset.cfState = 'playing';
        });
        player.addEventListener('pause', () => {
          if (frame.getAttribute('src') !== src || frame.dataset.cfState !== 'playing') return;
          frame.dataset.cfState = 'paused';
          this.#wake();
        });
        player.addEventListener('ended', () => {
          if (frame.getAttribute('src') !== src) return;
          frame.dataset.cfState = 'ended';
          this.#wake();
          if (frame === this.#cfCurrent && !this.#pipWindow) this.#startUpNext();
        });
      }).catch(() => {});
    };
    frame.addEventListener('load', attach, { signal: this.#ac.signal });
    attach();
  }

  #pauseCloudflare(frame) {
    if (this.#cfCurrent === frame) this.#cfCurrent = null;
    if (!frame.getAttribute('src') || frame.getAttribute('src') === 'about:blank') return;
    frame.src = 'about:blank';
    delete frame.dataset.cfState;
    delete frame.dataset.cfAttached;
  }

  #play(video) {
    this.#current = video;
    this.#applyResume(video);
    this.#currentEmbed = null;
    this.#stopOthers(video);
    // Always start with sound on; the browser decides if that's allowed yet.
    video.muted = false;
    const attempt = video.play();
    if (!attempt) return;
    attempt.then(() => {
      delete video.dataset.autoMuted;
      this.#syncPill();
    }).catch((error) => {
      // Interrupted because the viewer already moved to another episode: leave it stopped.
      if (this.#current !== video) return;
      // Still the episode on screen but its source was swapped (for example a quality change): just try again.
      if (error?.name === 'AbortError') {
        video.play().catch(() => {});
        return;
      }
      video.muted = true;
      video.dataset.autoMuted = 'true';
      video.play().catch(() => {});
      this.#syncPill();
    });
  }

  #embedSoundAsked = false;

  #unmute(event) {
    // Linked episodes: the first touch turns sound on once; after that the sound button is in charge.
    if (this.#currentEmbed && !this.#embedSoundAsked && this.#canUnmute(this.#currentEmbed) && !event?.target?.closest?.('[data-embed-bar]')) {
      this.#embedSoundAsked = true;
      this.#send(this.#currentEmbed, 'unmute');
    }
    const video = this.#current;
    if (!video?.dataset.autoMuted) return;
    delete video.dataset.autoMuted;
    video.muted = false;
    sessionStorage.setItem('horhy-sound', 'on');
    // If the browser still refuses sound, keep the video playing muted and show the pill again.
    video.play().catch(() => {
      video.muted = true;
      video.dataset.autoMuted = 'true';
      video.play().catch(() => {});
      this.#syncPill();
    });
    this.#syncPill();
  }

  #syncPill() {
    const pill = this.querySelector('[data-unmute]');
    if (pill) pill.hidden = !(this.#current?.dataset.autoMuted || this.#cfCurrent?.dataset.autoMuted);
  }

  #currentEmbed = null;
  #resume = null;

  // iPhone / iPad: Safari only lets a YouTube or Vimeo video play with sound (and obey the website's buttons
  // reliably) after the viewer has tapped the video itself once. Until then the video takes taps directly;
  // after that first tap, the website's own controls are in charge.
  // Clean screen: while an episode plays, every icon and the control panel fade away 3 seconds after the
  // last tap, touch, mouse move or key. While paused they stay on screen.
  #idleTimer = 0;

  #wake() {
    this.removeAttribute('data-idle');
    clearTimeout(this.#idleTimer);
    const check = () => {
      // Only hide while the video is really playing; otherwise look again shortly (it may still be starting).
      if (this.#isPlaying()) this.setAttribute('data-idle', '');
      else this.#idleTimer = setTimeout(check, 1000);
    };
    this.#idleTimer = setTimeout(check, 3000);
  }

  #isPlaying() {
    const video = this.#current;
    if (video) return !video.paused && !video.ended;
    // Cloudflare plays inside its own player, so an on-screen Cloudflare episode counts as playing.
    if (this.#cfCurrent) return this.#cfCurrent.dataset.cfState === 'playing';
    return this.#currentEmbed?.dataset.playing === 'true';
  }

  #setupIdle() {
    const signal = this.#ac.signal;
    // Any activity while the icons are showing restarts the 30-second timer.
    const active = () => {
      if (!this.hasAttribute('data-idle')) this.#wake();
    };
    this.addEventListener('pointerdown', active, { capture: true, signal });
    this.addEventListener('pointermove', (event) => { if (event.pointerType === 'mouse') active(); }, { signal });
    this.addEventListener('wheel', active, { passive: true, signal });
    // Keyboard users always get the controls back.
    document.addEventListener('keydown', () => this.#wake(), { signal });
    // Touches and clicks inside the Cloudflare player never reach the page. When one moves focus into it,
    // show the icons again, then hand focus back so the next touch is noticed too.
    window.addEventListener('blur', () => {
      setTimeout(() => {
        const frame = document.activeElement;
        if (!frame?.matches?.('iframe[data-cloudflare]') || !this.contains(frame)) return;
        this.#wake();
        setTimeout(() => window.focus(), 300);
      }, 0);
    }, { signal });
    signal.addEventListener('abort', () => clearTimeout(this.#idleTimer));
    this.#wake();
  }

  #togglePlayAt(tap) {
    const video = tap.parentElement.querySelector('video');
    if (video) {
      this.#toggleVideo(video);
      return;
    }
    const embed = tap.parentElement.querySelector('iframe[data-yt], iframe[data-vimeo]');
    if (embed) this.#toggleEmbed(embed);
  }


  #ios = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  #canUnmute(embed) {
    return !this.#ios || embed.dataset.touched === 'true';
  }

  #setupTapThrough() {
    if (!this.#ios) return;
    // Tapping into the video moves focus into it; that's how we know the first tap happened.
    window.addEventListener('blur', () => {
      setTimeout(() => {
        const embed = document.activeElement;
        if (!embed?.matches?.('iframe[data-yt], iframe[data-vimeo]') || !this.contains(embed)) return;
        this.removeAttribute('data-idle');
        if (embed.dataset.touched === 'true') return;
        embed.dataset.touched = 'true';
        this.#syncTapThrough(embed);
        this.#send(embed, 'unmute');
        this.#send(embed, 'play');
        setTimeout(() => window.focus(), 300);
      }, 0);
    }, { signal: this.#ac.signal });
  }

  #syncTapThrough(embed) {
    if (!this.#ios) return;
    const tap = embed.parentElement?.querySelector('[data-yt-tap]');
    const direct = embed.dataset.touched !== 'true';
    embed.style.pointerEvents = direct ? 'auto' : '';
    if (tap) tap.style.pointerEvents = direct ? 'none' : '';
  }

  // Linked episodes wait with their address in data-src until needed; this starts loading one.
  #loadEmbed(slide) {
    const embed = slide.querySelector('iframe[data-src]:not([src])');
    if (embed) embed.src = embed.dataset.src;
  }

  // Play/pause from the website's own controls. The button follows what the video is really doing
  // (reported by YouTube/Vimeo), never what we merely asked for.
  #toggleEmbed(embed) {
    if (embed.dataset.playing === 'true') {
      // A viewer's pause wins over any start-up retries still running.
      embed.dataset.confirmed = 'true';
      this.#send(embed, 'pause');
      return;
    }
    const pressedAt = Date.now();
    this.#send(embed, 'play');
    if (embed.dataset.muted !== 'false' && this.#canUnmute(embed)) this.#send(embed, 'unmute');
    // If the browser refuses to start with sound, start muted instead (the sound button turns it on).
    setTimeout(() => {
      if ((Number(embed.dataset.progressAt) || 0) >= pressedAt) return;
      this.#send(embed, 'mute');
      this.#send(embed, 'play');
    }, 1500);
  }

  // Every second: a linked video that has stopped moving for a few seconds shows as paused,
  // so the play button never gets stuck on the wrong icon.
  #watchEmbeds() {
    const timer = setInterval(() => {
      for (const embed of [this.#currentEmbed, this.#pipEmbed]) {
        if (!embed || embed.dataset.playing !== 'true') continue;
        const last = Number(embed.dataset.progressAt) || 0;
        if (last && Date.now() - last > 2500) {
          embed.dataset.playing = 'false';
          this.#syncBar(embed);
        }
      }
    }, 1000);
    this.#ac.signal.addEventListener('abort', () => clearInterval(timer));
  }

  // Asks a YouTube or Vimeo player to report its state (playing, paused, time, ended) back to the page.
  #handshake(embed) {
    let tries = 0;
    const ping = () => {
      if (embed.dataset.ready === 'true' || tries > 20 || !embed.isConnected) return;
      tries += 1;
      const target = embed.contentWindow;
      if (target) {
        if (embed.hasAttribute('data-yt')) {
          target.postMessage(JSON.stringify({ event: 'listening', id: 'reel', channel: 'widget' }), '*');
          target.postMessage(JSON.stringify({ event: 'command', func: 'addEventListener', args: ['onStateChange'], id: 'reel', channel: 'widget' }), '*');
        } else {
          for (const value of ['ended', 'play', 'pause', 'timeupdate']) {
            target.postMessage(JSON.stringify({ method: 'addEventListener', value }), '*');
          }
        }
      }
      setTimeout(ping, 1000);
    };
    ping();
  }

  // Messages from YouTube / Vimeo players, on this page or in the pop-out window.
  #onEmbedMessage(event) {
    if (!/youtube|vimeo/.test(event.origin)) return;
    let data = event.data;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch { return; }
    }
    const frames = [...this.querySelectorAll('iframe[data-yt], iframe[data-vimeo]')];
    if (this.#pipEmbed) frames.push(this.#pipEmbed);
    const embed = frames.find((frame) => frame.contentWindow === event.source);
    if (!embed) return;

    if (embed.dataset.ready !== 'true') {
      embed.dataset.ready = 'true';
      if (embed.hasAttribute('data-vimeo')) {
        for (const value of ['ended', 'play', 'pause', 'timeupdate']) {
          embed.contentWindow?.postMessage(JSON.stringify({ method: 'addEventListener', value }), '*');
        }
      }
    }

    // YouTube sends playerState / currentTime / duration; Vimeo sends play, pause, timeupdate and ended events.
    const info = data?.info && typeof data.info === 'object' ? data.info : null;
    const state = info?.playerState ?? (data?.event === 'onStateChange' ? data.info : undefined);
    const vimeoTime = data?.event === 'timeupdate' ? data.data : null;
    const duration = Number(info?.duration ?? vimeoTime?.duration ?? embed.dataset.duration ?? 0);
    const time = info?.currentTime ?? vimeoTime?.seconds;
    if (duration > 0) embed.dataset.duration = String(duration);
    if (typeof time === 'number') {
      // Time moving forward is the surest sign the video is really playing.
      const previous = Number(embed.dataset.time);
      if (Number.isFinite(previous) && time > previous + 0.05) {
        embed.dataset.progressAt = String(Date.now());
        embed.dataset.playing = 'true';
        embed.dataset.confirmed = 'true';
      }
      embed.dataset.time = String(time);
    }

    if (state === 1 || data?.event === 'play' || (vimeoTime && vimeoTime.seconds > 0)) {
      embed.dataset.playing = 'true';
      embed.dataset.confirmed = 'true';
    }
    if (state === 2 || data?.event === 'pause') embed.dataset.playing = 'false';
    // A linked episode that starts late, after the viewer moved on, is stopped straight away.
    if (embed.dataset.playing === 'true' && embed !== this.#currentEmbed && embed !== this.#pipEmbed) this.#send(embed, 'pause');
    if (typeof info?.muted === 'boolean') embed.dataset.muted = String(info.muted);
    this.#syncBar(embed);

    let ended = state === 0 || data?.event === 'ended' || data?.event === 'finish';
    // Backup for players that never send "ended": the last half-second counts as the end.
    if (!ended && typeof time === 'number' && duration > 1 && time >= duration - 0.5) ended = true;
    if (typeof time === 'number' && duration > 1 && time < duration - 2) delete embed.dataset.ended;
    if (ended) this.#embedEnded(embed);
  }

  #embedEnded(embed) {
    if (embed.dataset.ended === 'true') return;
    embed.dataset.ended = 'true';
    embed.dataset.playing = 'false';
    this.#syncBar(embed);
    if (embed === this.#pipEmbed) this.#pipEnded();
    else if (embed === this.#currentEmbed && !this.#pipWindow) this.#startUpNext();
  }

  // Official YouTube / Vimeo player libraries: the most reliable way for the website's own controls to
  // drive a linked video and read back what it's really doing. Each library loads once, only when needed.
  #scripts = {};
  #players = new WeakMap();
  #playerReady = new WeakMap();

  #loadScript(src) {
    if (!this.#scripts[src]) {
      this.#scripts[src] = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.append(script);
      });
    }
    return this.#scripts[src];
  }

  #loadYouTube() {
    if (window.YT?.Player) return Promise.resolve();
    if (!this.#scripts.yt) {
      this.#scripts.yt = new Promise((resolve) => {
        const previous = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          previous?.();
          resolve();
        };
        this.#loadScript('https://www.youtube.com/iframe_api').catch(() => {});
      });
    }
    return this.#scripts.yt;
  }

  #connectPlayer(embed) {
    if (this.#players.has(embed) || embed.ownerDocument !== document || !embed.getAttribute('src')) return;
    const connecting = (async () => {
      try {
        if (embed.hasAttribute('data-yt')) {
          await this.#loadYouTube();
          const player = await new Promise((resolve) => {
            const created = new window.YT.Player(embed, {
              events: {
                onReady: () => resolve(created),
                onStateChange: (event) => {
                  if (event.data === 0) this.#embedEnded(embed);
                },
              },
            });
            setTimeout(() => resolve(created), 8000);
          });
          this.#playerReady.set(embed, { type: 'yt', player });
        } else {
          await this.#loadScript('https://player.vimeo.com/api/player.js');
          const player = new window.Vimeo.Player(embed);
          await player.ready().catch(() => {});
          player.on('ended', () => this.#embedEnded(embed));
          this.#playerReady.set(embed, { type: 'vimeo', player });
        }
      } catch {
        // Library blocked: the basic message connection keeps working.
      }
    })();
    this.#players.set(embed, connecting);
  }

  // Four times a second, read the real time, length, play state and sound from the playing linked video.
  #pollPlayers() {
    let busy = false;
    const timer = setInterval(async () => {
      const embed = this.#currentEmbed;
      const ready = embed && this.#playerReady.get(embed);
      if (!ready || busy) return;
      busy = true;
      try {
        let time = 0;
        let duration = 0;
        let playing = false;
        let muted = true;
        let ended = false;
        if (ready.type === 'yt') {
          const p = ready.player;
          if (typeof p.getCurrentTime !== 'function') return;
          time = p.getCurrentTime() || 0;
          duration = p.getDuration() || 0;
          const state = p.getPlayerState();
          playing = state === 1 || state === 3;
          ended = state === 0;
          muted = p.isMuted();
        } else {
          const p = ready.player;
          [time, duration, muted, ended] = await Promise.all([p.getCurrentTime(), p.getDuration(), p.getMuted(), p.getEnded()]);
          playing = !(await p.getPaused());
        }
        if (playing) {
          embed.dataset.progressAt = String(Date.now());
          embed.dataset.confirmed = 'true';
          delete embed.dataset.ended;
        }
        embed.dataset.playing = String(playing);
        embed.dataset.time = String(time);
        if (duration > 0) embed.dataset.duration = String(duration);
        embed.dataset.muted = String(Boolean(muted));
        this.#syncBar(embed);
        if (ended) this.#embedEnded(embed);
      } catch {
        // Player busy or gone: try again next tick.
      } finally {
        busy = false;
      }
    }, 250);
    this.#ac.signal.addEventListener('abort', () => clearInterval(timer));
  }

  // Pop-out player (picture-in-picture): floats over other apps and tabs on laptops and phones that support it.
  #setupPip() {
    if (!document.pictureInPictureEnabled) return;
    // The pop-out button lives in each episode's control panel (the top-bar one only carries its labels).
    this.querySelectorAll('[data-embed-pip]').forEach((button) => { button.hidden = false; });
    // Chrome/Edge can pop the video out automatically when the viewer switches tabs or apps while it plays.
    try {
      navigator.mediaSession?.setActionHandler('enterpictureinpicture', () => {
        if (!this.#openPipWindow()) this.#current?.requestPictureInPicture().catch(() => {});
      });
    } catch {
      // Older browsers: the pop-out button still works.
    }
  }

  async #togglePip() {
    if (this.#pipWindow) {
      this.#pipWindow.close();
      return;
    }
    if (this.#openPipWindow()) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (this.#current) {
        await this.#current.requestPictureInPicture();
      }
    } catch {
      // Blocked (for example a YouTube episode): nothing to do.
    }
  }

  // Scrollable pop-out (Chrome and Edge on laptops): a floating window the site draws itself, so viewers can
  // scroll, swipe or use the arrow keys inside it to move between episodes, TikTok-style.
  // Other browsers fall back to the standard pop-out window with next/previous buttons.
  #pipWindow = null;
  #pipVideo = null;
  #pipFinished = false;

  #openPipWindow() {
    if (!('documentPictureInPicture' in window)) return false;
    const video = this.#current;
    const embed = this.#currentEmbed;
    const slide = (video || embed)?.closest('[data-episode]');
    if (!slide) return false;
    const source = slide.querySelector('[data-mini-src]');
    if (video && !source) return false;
    let wide = source?.dataset.miniWide === 'true';
    let startTime = video?.currentTime || 0;
    if (embed) {
      // Linked videos (YouTube / Vimeo) are usually landscape.
      wide = true;
      startTime = Number(embed.dataset.time) || 0;
    }
    window.documentPictureInPicture.requestWindow(wide ? { width: 640, height: 360 } : { width: 360, height: 640 })
      .then((pip) => this.#buildPipWindow(pip, slide, startTime))
      .catch(() => { video?.requestPictureInPicture?.().catch(() => {}); });
    return true;
  }

  #pipEmbed = null;

  #buildPipWindow(pip, slide, startTime) {
    const labels = this.querySelector('[data-pip]')?.dataset || {};
    this.#pipWindow = pip;
    const doc = pip.document;
    doc.title = document.title;
    const style = doc.createElement('style');
    style.textContent = `
      html,body{margin:0;height:100%;background:#000;color:#fff;font-family:system-ui,sans-serif;overflow:hidden;touch-action:none}
      video,iframe{position:fixed;inset:0;width:100%;height:100%;object-fit:contain;display:block;border:0;background:#000}
      .cover{position:fixed;inset:0 0 56px;cursor:pointer;background:transparent;border:0}
      .top{position:fixed;inset:0 0 auto;padding:10px 12px;background:linear-gradient(#000a,#0000);font-size:13px;font-weight:700;pointer-events:none;z-index:2}
      .hint{position:fixed;inset:auto 0 64px;text-align:center;font-size:12px;font-weight:700;opacity:.85;pointer-events:none;transition:opacity .6s;text-shadow:0 1px 3px #000;z-index:2}
      .lock{position:fixed;inset:0;display:grid;place-items:center;padding:24px;text-align:center;font-weight:700;background:#000c;z-index:3}
      [hidden]{display:none!important}`;
    doc.head.append(style);
    const top = doc.createElement('div');
    top.className = 'top';
    const player = doc.createElement('video');
    player.controls = true;
    player.playsInline = true;
    player.setAttribute('controlslist', 'nodownload noremoteplayback');
    // Linked videos play in their own frame, which swallows scrolling; this clear layer sits over the picture
    // (not the bottom control bar) so scrolling still switches episodes and a click plays or pauses.
    const cover = doc.createElement('button');
    cover.type = 'button';
    cover.className = 'cover';
    cover.hidden = true;
    cover.setAttribute('aria-label', labels.pipSwipe || '');
    cover.addEventListener('click', () => { if (this.#pipEmbed) this.#toggleEmbed(this.#pipEmbed); });
    const hint = doc.createElement('div');
    hint.className = 'hint';
    hint.textContent = `↕ ${labels.pipSwipe || ''}`;
    const lock = doc.createElement('div');
    lock.className = 'lock';
    lock.hidden = true;
    lock.textContent = labels.pipLocked || '';
    doc.body.append(player, cover, top, hint, lock);
    this.#pipVideo = player;
    setTimeout(() => { hint.style.opacity = '0'; }, 3500);
    pip.addEventListener('message', (event) => this.#onEmbedMessage(event));

    // The page's own player pauses; the pop-out takes over at the same second.
    this.#current?.pause();
    if (this.#currentEmbed) this.#send(this.#currentEmbed, 'pause');
    this.#showInPip(slide, startTime);

    // Ended episodes roll straight into the next one.
    player.addEventListener('ended', () => this.#pipEnded());

    let lockUntil = 0;
    const step = (direction) => {
      if (Date.now() < lockUntil) return;
      lockUntil = Date.now() + 700;
      this.#pipStep(direction);
    };
    doc.addEventListener('wheel', (event) => {
      event.preventDefault();
      if (Math.abs(event.deltaY) < 20 || Math.abs(event.deltaY) < Math.abs(event.deltaX)) return;
      step(event.deltaY > 0 ? 1 : -1);
    }, { passive: false });
    let startY = null;
    let startX = 0;
    doc.addEventListener('touchstart', (event) => { startY = event.touches[0].clientY; startX = event.touches[0].clientX; }, { passive: true });
    doc.addEventListener('touchend', (event) => {
      if (startY === null) return;
      const distance = startY - event.changedTouches[0].clientY;
      const sideways = Math.abs(event.changedTouches[0].clientX - startX);
      startY = null;
      if (Math.abs(distance) > 50 && Math.abs(distance) > sideways) step(distance > 0 ? 1 : -1);
    }, { passive: true });
    doc.addEventListener('keydown', (event) => {
      const keys = { ArrowDown: 1, PageDown: 1, ArrowUp: -1, PageUp: -1 };
      if (!(event.key in keys)) return;
      event.preventDefault();
      step(keys[event.key]);
    });

    // Closing the pop-out brings the episode back to the page at the same second.
    pip.addEventListener('pagehide', () => {
      const episode = this.#pipWindow && this.#pipVideo?.dataset.episode;
      const time = this.#pipEmbed ? Number(this.#pipEmbed.dataset.time) || 0 : this.#pipVideo?.currentTime || 0;
      this.#pipWindow = null;
      this.#pipVideo = null;
      this.#pipEmbed = null;
      if (this.#pipFinished) {
        this.#pipFinished = false;
        this.#startNextMovie();
        return;
      }
      const back = this.querySelector(`[data-reel-feed] [data-episode="${episode}"]`);
      if (!back) return;
      this.#scrollToSlide(back, true);
      this.#setCurrent(episode);
      if (back.hasAttribute('data-locked')) return;
      const video = back.querySelector('video');
      const embed = back.querySelector('iframe[data-yt], iframe[data-vimeo]');
      if (video) {
        video.currentTime = time;
        this.#play(video);
      } else if (embed) {
        this.#playEmbed(embed);
        this.#seekEmbed(embed, time);
      }
    });
  }

  // Linked videos: jump to a second (used when coming back from the pop-out).
  #seekEmbed(embed, time) {
    if (!time) return;
    [300, 1200].forEach((delay) => setTimeout(() => {
      const target = embed.contentWindow;
      if (!target) return;
      if (embed.hasAttribute('data-yt')) target.postMessage(JSON.stringify({ event: 'command', func: 'seekTo', args: [time, true] }), '*');
      else target.postMessage(JSON.stringify({ method: 'setCurrentTime', value: time }), '*');
    }, delay));
  }

  // An episode finished in the pop-out: play the next one, or after the last one close and offer more movies.
  #pipEnded() {
    if (this.#nextSlide()) {
      this.#pipStep(1);
      return;
    }
    this.#pipFinished = true;
    this.#pipWindow?.close();
  }

  #showInPip(slide, startTime = 0) {
    const player = this.#pipVideo;
    const doc = this.#pipWindow?.document;
    if (!player || !doc) return;
    const labels = this.querySelector('[data-pip]')?.dataset || {};
    const source = slide.querySelector('[data-mini-src]');
    const pageEmbed = slide.querySelector('iframe[data-yt], iframe[data-vimeo]');
    const lock = doc.querySelector('.lock');
    const cover = doc.querySelector('.cover');
    doc.querySelector('.top').textContent = (labels.pipEpisode || '#').replace('#', slide.dataset.episode);
    player.dataset.episode = slide.dataset.episode;
    // Keep the page in step, so closing the pop-out lands on the same episode.
    this.#scrollToSlide(slide, true);
    this.#setCurrent(slide.dataset.episode);

    // Clear whatever was showing.
    player.pause();
    player.removeAttribute('src');
    player.hidden = true;
    this.#pipEmbed?.remove();
    this.#pipEmbed = null;
    cover.hidden = true;

    if (slide.hasAttribute('data-locked') || (!source && !pageEmbed)) {
      lock.hidden = false;
      return;
    }
    lock.hidden = true;

    if (pageEmbed) {
      // Same YouTube / Vimeo episode, starting on its own with sound, with the player's bar for seeking.
      const url = new URL(pageEmbed.src || pageEmbed.dataset.src, location.href);
      const youtube = pageEmbed.hasAttribute('data-yt');
      url.searchParams.set('autoplay', '1');
      url.searchParams.set('controls', '1');
      if (startTime) {
        if (youtube) url.searchParams.set('start', String(Math.floor(startTime)));
        else url.hash = `t=${Math.floor(startTime)}s`;
      }
      const frame = doc.createElement('iframe');
      if (youtube) frame.setAttribute('data-yt', '');
      else frame.setAttribute('data-vimeo', '');
      frame.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
      frame.title = pageEmbed.title;
      frame.src = url.toString();
      doc.body.prepend(frame);
      this.#pipEmbed = frame;
      // Starts muted (browsers allow that), then turns sound on: the viewer already clicked the pop-out button.
      frame.addEventListener('load', () => {
        this.#handshake(frame);
        [500, 1500, 3000].forEach((delay) => setTimeout(() => {
          if (this.#pipEmbed !== frame) return;
          this.#send(frame, 'play');
          this.#send(frame, 'unmute');
        }, delay));
      });
      cover.hidden = false;
      return;
    }

    player.hidden = false;
    player.poster = source.dataset.miniPoster || '';
    player.src = source.dataset.miniSrc;
    if (startTime) player.addEventListener('loadedmetadata', () => { player.currentTime = startTime; }, { once: true });
    player.play().catch(() => { player.muted = true; player.play().catch(() => {}); });
  }

  #pipStep(direction) {
    const target = direction > 0 ? this.#nextSlide() : this.#prevSlide();
    if (target) this.#showInPip(target);
  }

  // Next / previous episode buttons in the pop-out window, lock screen and headphones.
  #setupEpisodeKeys() {
    const handlers = {
      nexttrack: () => { if (this.#pipWindow) return this.#pipStep(1); const next = this.#nextSlide(); if (next) this.#goTo(next); },
      previoustrack: () => { if (this.#pipWindow) return this.#pipStep(-1); const prev = this.#prevSlide(); if (prev) this.#goTo(prev); },
    };
    for (const [action, handler] of Object.entries(handlers)) {
      try { navigator.mediaSession?.setActionHandler(action, handler); } catch { /* not supported */ }
    }
  }

  // TikTok-style episode switching on tablets, where the big player doesn't scroll on its own:
  // a swipe up or down on the player moves one episode at a time. Laptops (mouse / trackpad) change episodes only from the episode list.
  #swipeLock = false;

  #step(direction) {
    if (this.#swipeLock) return;
    const target = direction > 0 ? this.#nextSlide() : this.#prevSlide();
    if (!target) {
      // Scrolling down past the last episode offers the next movie.
      if (direction > 0 && this.querySelector('[data-next-movie]')?.hidden) this.#startNextMovie();
      return;
    }
    this.#swipeLock = true;
    setTimeout(() => { this.#swipeLock = false; }, 700);
    this.#stopUpNext();
    this.#goTo(target);
  }

  #setupSwipe(feed) {
    const locked = () => getComputedStyle(feed).overflowY === 'hidden';
    const signal = this.#ac.signal;


    // Only clearly vertical swipes switch episodes, so dragging the video's timeline still works.
    let startY = null;
    let startX = 0;
    feed.addEventListener('touchstart', (event) => {
      startY = locked() ? event.touches[0].clientY : null;
      startX = event.touches[0].clientX;
    }, { passive: true, signal });
    feed.addEventListener('touchmove', (event) => {
      if (startY === null) return;
      const dy = Math.abs(event.touches[0].clientY - startY);
      const dx = Math.abs(event.touches[0].clientX - startX);
      if (dy > dx) event.preventDefault();
    }, { passive: false, signal });
    feed.addEventListener('touchend', (event) => {
      if (startY === null) return;
      const distance = startY - event.changedTouches[0].clientY;
      const sideways = Math.abs(event.changedTouches[0].clientX - startX);
      startY = null;
      if (Math.abs(distance) > 50 && Math.abs(distance) > sideways) this.#step(distance > 0 ? 1 : -1);
    }, { passive: true, signal });

  }

  // Mini player hand-off: save the playing episode and time, then leave; <s-mini-player> keeps it playing on other pages.
  #minimize(button) {
    const home = button?.dataset.home || '/';
    const video = this.#current;
    const slide = video?.closest('[data-episode]');
    const data = slide?.querySelector('[data-mini-src]');
    if (video && data) {
      try {
        sessionStorage.setItem('horhy-mini', JSON.stringify({
          src: data.dataset.miniSrc,
          poster: data.dataset.miniPoster || '',
          wide: data.dataset.miniWide === 'true',
          title: button?.dataset.title || document.title,
          path: location.pathname,
          episode: slide.dataset.episode,
          time: video.currentTime || 0,
          // Every unlocked uploaded episode, so the mini player can switch and auto-play the next one.
          episodes: [...this.querySelectorAll('[data-reel-feed] [data-episode]')].map((item) => {
            const source = item.querySelector('[data-mini-src]');
            return {
              episode: item.dataset.episode,
              src: source?.dataset.miniSrc || '',
              poster: source?.dataset.miniPoster || '',
              wide: source?.dataset.miniWide === 'true',
            };
          }),
        }));
      } catch {
        // Storage unavailable: just leave the page.
      }
      video.pause();
    }
    let sameSite = false;
    try { sameSite = Boolean(document.referrer) && new URL(document.referrer).origin === location.origin && new URL(document.referrer).pathname !== location.pathname; } catch { sameSite = false; }
    if (sameSite && history.length > 1) history.back();
    else location.href = home;
  }

  // Coming back from the mini player: start the same episode at the same second.
  // Opening a movie without a chosen episode: start where this device last watched it ("Continue watching"),
  // or, on a new device, at the first episode the viewer unlocked with credits (saved on their account).
  #startAtSaved(feed) {
    if (location.hash || this.#resume) return;
    let episode = 0;
    try {
      const history = JSON.parse(localStorage.getItem('horhy-history') || '[]');
      const saved = Array.isArray(history) ? history.find((item) => String(item.id) === this.dataset.movieId) : null;
      episode = Number(saved?.episode) || 0;
    } catch {
      episode = 0;
    }
    if (!episode) episode = Number(this.querySelector('[data-account-start]')?.dataset.accountStart) || 0;
    if (episode <= 1) return;
    const slide = feed.querySelector(`[data-episode="${episode}"]`);
    if (slide) feed.scrollTo({ top: slide.offsetTop, behavior: 'auto' });
  }

  #readResume() {
    sessionStorage.removeItem('horhy-mini');
    try {
      const resume = JSON.parse(sessionStorage.getItem('horhy-resume') || 'null');
      sessionStorage.removeItem('horhy-resume');
      if (resume && resume.path === location.pathname) this.#resume = resume;
    } catch {
      this.#resume = null;
    }
  }

  #applyResume(video) {
    const resume = this.#resume;
    if (!resume) return;
    if (video.closest('[data-episode]')?.dataset.episode !== String(resume.episode)) return;
    this.#resume = null;
    const seek = () => { video.currentTime = Number(resume.time) || 0; };
    if (video.readyState >= 1) seek();
    else video.addEventListener('loadedmetadata', seek, { once: true });
  }

  // Starts a YouTube/Vimeo episode muted (browsers only allow that), then turns sound on once the viewer has touched the page.
  #playEmbed(embed) {
    this.#current = null;
    this.#currentEmbed = embed;
    delete embed.dataset.ended;
    if (this.#pipWindow) return;
    this.#stopOthers(embed);
    this.#loadEmbed(embed.closest('[data-episode]') || embed.parentElement);
    this.#connectPlayer(embed);
    this.#syncTapThrough(embed);
    // Players ignore commands until they are ready, so keep asking (for up to ~12 seconds)
    // until the player itself reports that it's playing.
    delete embed.dataset.confirmed;
    let tries = 0;
    const attempt = () => {
      if (this.#currentEmbed !== embed || this.#pipWindow || embed.dataset.confirmed === 'true' || tries > 16) return;
      tries += 1;
      this.#send(embed, 'play');
      if (navigator.userActivation?.hasBeenActive && this.#canUnmute(embed) && embed.dataset.muted !== 'false' && tries <= 3) this.#send(embed, 'unmute');
      setTimeout(attempt, tries < 4 ? 500 : 900);
    };
    attempt();
  }

  #send(embed, action) {
    const target = embed.contentWindow;
    if (!target) return;
    if (action === 'pause') embed.dataset.playing = 'false';
    if (action === 'mute') embed.dataset.muted = 'true';
    if (action === 'unmute') embed.dataset.muted = 'false';
    const ready = this.#playerReady.get(embed);
    if (ready?.type === 'yt' && typeof ready.player.playVideo === 'function') {
      const p = ready.player;
      ({ play: () => p.playVideo(), pause: () => p.pauseVideo(), mute: () => p.mute(), unmute: () => { p.unMute(); p.setVolume(Math.round(Number(embed.dataset.volume || 1) * 100) || 100); } })[action]?.();
    } else if (ready?.type === 'vimeo') {
      const p = ready.player;
      const run = { play: () => p.play(), pause: () => p.pause(), mute: () => p.setMuted(true), unmute: () => p.setMuted(false).then(() => p.setVolume(1)) }[action];
      run?.().catch?.(() => {});
    } else if (embed.hasAttribute('data-yt')) {
      const func = { play: 'playVideo', pause: 'pauseVideo', unmute: 'unMute', mute: 'mute' }[action];
      target.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*');
    } else if (action === 'unmute' || action === 'mute') {
      target.postMessage(JSON.stringify({ method: 'setMuted', value: action === 'mute' }), '*');
      if (action === 'unmute') target.postMessage(JSON.stringify({ method: 'setVolume', value: 1 }), '*');
    } else {
      target.postMessage(JSON.stringify({ method: action }), '*');
    }
    this.#syncBar(embed);
  }

  #seekTo(embed, time) {
    const target = embed.contentWindow;
    if (!target) return;
    embed.dataset.time = String(time);
    const ready = this.#playerReady.get(embed);
    if (ready?.type === 'yt' && typeof ready.player.seekTo === 'function') ready.player.seekTo(time, true);
    else if (ready?.type === 'vimeo') ready.player.setCurrentTime(time).catch(() => {});
    else if (embed.hasAttribute('data-yt')) target.postMessage(JSON.stringify({ event: 'command', func: 'seekTo', args: [time, true] }), '*');
    else target.postMessage(JSON.stringify({ method: 'setCurrentTime', value: time }), '*');
    this.#syncBar(embed);
  }

  // Keeps a linked episode's control bar (play/pause, time, progress, sound) in step with its player.
  #toggleVideo(video) {
    if (video.paused || video.ended) {
      this.#current = video;
      this.#stopOthers(video);
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    } else {
      video.pause();
    }
  }

  // Works for both kinds of episode: an uploaded <video> or a YouTube/Vimeo player.
  #syncBar(embed) {
    const slide = embed.closest('[data-episode]');
    const bar = slide?.querySelector('[data-embed-bar]');
    if (!bar) return;
    const isVideo = embed instanceof HTMLVideoElement;
    const playing = isVideo ? !embed.paused && !embed.ended : embed.dataset.playing === 'true';
    const muted = isVideo ? embed.muted : embed.dataset.muted !== 'false';
    const time = isVideo ? embed.currentTime || 0 : Number(embed.dataset.time) || 0;
    const duration = isVideo ? (Number.isFinite(embed.duration) ? embed.duration : 0) : Number(embed.dataset.duration) || 0;
    const clock = (seconds) => {
      const total = Math.max(0, Math.floor(seconds));
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const secs = String(total % 60).padStart(2, '0');
      return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${secs}` : `${String(minutes).padStart(2, '0')}:${secs}`;
    };
    // Play/pause is a tap on the picture (TikTok style); its label follows what the video is doing.
    const tap = slide.querySelector('[data-yt-tap]');
    tap?.setAttribute('aria-label', playing ? bar.dataset.labelPause : bar.dataset.labelPlay);
    const mute = bar.querySelector('[data-embed-mute]');
    mute.querySelector('[data-icon-sound]').hidden = muted;
    mute.querySelector('[data-icon-muted]').hidden = !muted;
    mute.setAttribute('aria-pressed', String(muted));
    mute.setAttribute('aria-label', muted ? bar.dataset.labelUnmute : bar.dataset.labelMute);
    bar.querySelector('[data-embed-time]').textContent = clock(time);
    bar.querySelector('[data-embed-duration]').textContent = clock(duration);
    const seek = bar.querySelector('[data-embed-seek]');
    if (duration > 0) seek.max = String(duration);
    if (!seek.matches(':active')) seek.value = String(time);
    seek.style.setProperty('--fill', `${duration > 0 ? Math.min(100, (Number(seek.value) / duration) * 100) : 0}%`);
    // Volume slider and speed label.
    const volumeInput = bar.querySelector('[data-embed-volume]');
    if (volumeInput && !volumeInput.matches(':active')) {
      const volume = muted ? 0 : (isVideo ? embed.volume : Number(embed.dataset.volume ?? 1));
      volumeInput.value = String(volume);
      volumeInput.style.setProperty('--fill', `${volume * 100}%`);
    }
    const speedLabel = bar.querySelector('[data-embed-speed-label]');
    const rate = isVideo ? embed.playbackRate : Number(embed.dataset.rate || 1);
    if (speedLabel) speedLabel.textContent = `${Number.isInteger(rate) ? rate.toFixed(1) : rate}x`;
    const big = slide.querySelector('[data-embed-big]');
    if (big) big.hidden = playing || time === 0;
    // Thumbnail over YouTube's start and end screens.
    const cover = slide.querySelector('[data-embed-cover]');
    // Stays up for the first moment of playback too, while YouTube flashes its own start-up buttons.
    const coverDelay = embed?.hasAttribute?.('data-vimeo') ? 0.2 : 1.5;
    if (cover) cover.hidden = time > coverDelay && embed.dataset.ended !== 'true';
  }

  #currentEpisode = null;
  #upNextTimer = null;

  #nextSlide() {
    const slides = [...this.querySelectorAll('[data-reel-feed] [data-episode]')];
    const index = slides.findIndex((slide) => slide.dataset.episode === this.#currentEpisode);
    return index >= 0 ? slides[index + 1] : null;
  }

  #startUpNext() {
    const next = this.#nextSlide();
    if (!next) {
      this.#startNextMovie();
      return;
    }
    const card = this.querySelector('[data-up-next]');
    if (!card) return;
    this.#stopUpNext();
    const seconds = 3;
    let left = seconds;
    const label = card.querySelector('[data-up-next-seconds]');
    const ring = card.querySelector('[data-up-next-ring]');
    const number = card.querySelector('[data-up-next-number]');
    if (number) number.textContent = next.dataset.episode;
    if (label) label.textContent = left;
    if (ring) ring.style.strokeDashoffset = '0';
    card.hidden = false;
    this.#upNextTimer = setInterval(() => {
      left -= 1;
      if (label) label.textContent = Math.max(left, 0);
      if (ring) ring.style.strokeDashoffset = String(((seconds - left) / seconds) * 100);
      if (left <= 0) this.#goNext();
    }, 1000);
  }

  // Last episode finished: count down, then open the next movie. "Stay here" cancels.
  #startNextMovie() {
    const card = this.querySelector('[data-next-movie]');
    if (!card?.dataset.url) return;
    this.#stopUpNext();
    let left = 5;
    const label = card.querySelector('[data-next-movie-seconds]');
    if (label) {
      label.textContent = left;
      label.parentElement.hidden = false;
    }
    card.hidden = false;
    this.#setupMovieRow(card, label);
    this.#upNextTimer = setInterval(() => {
      left -= 1;
      if (label) label.textContent = Math.max(left, 0);
      if (left <= 0) {
        this.#stopUpNext();
        location.href = card.dataset.url;
      }
    }, 1000);
  }

  // The movie row scrolls sideways: swipe, trackpad, or a normal mouse wheel. Touching it stops the countdown
  // so the viewer can take their time choosing.
  #movieRowReady = false;

  #setupMovieRow(card, label) {
    if (this.#movieRowReady) return;
    this.#movieRowReady = true;
    const row = card.querySelector('[data-next-movie-row]');
    const signal = this.#ac.signal;
    const choosing = () => {
      if (!this.#upNextTimer) return;
      clearInterval(this.#upNextTimer);
      this.#upNextTimer = null;
      if (label) label.parentElement.hidden = true;
    };
    for (const type of ['pointerdown', 'touchstart', 'focusin', 'keydown']) {
      card.addEventListener(type, choosing, { passive: true, signal });
    }
    row?.addEventListener('wheel', (event) => {
      choosing();
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      row.scrollBy({ left: event.deltaY, behavior: 'auto' });
    }, { passive: false, signal });
  }

  #stopUpNext() {
    clearInterval(this.#upNextTimer);
    this.#upNextTimer = null;
    this.querySelectorAll('[data-up-next], [data-next-movie]').forEach((card) => { card.hidden = true; });
  }

  #goNext() {
    const next = this.#nextSlide();
    this.#stopUpNext();
    if (next) this.#goTo(next);
  }

  #prevSlide() {
    const slides = [...this.querySelectorAll('[data-reel-feed] [data-episode]')];
    const index = slides.findIndex((slide) => slide.dataset.episode === this.#currentEpisode);
    return index > 0 ? slides[index - 1] : null;
  }

  // Scrolls only the episode feed (not the whole page), so the laptop playlist layout stays in place.
  #scrollToSlide(slide, reduce) {
    const feed = this.querySelector('[data-reel-feed]');
    if (!feed) return;
    feed.scrollTo({ top: slide.offsetTop, behavior: reduce ? 'auto' : 'smooth' });
  }

  // Moves to an episode. While popped out, the floating window switches straight to it;
  // a locked episode closes the pop-out so the viewer sees the unlock screen.
  #goTo(slide) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#scrollToSlide(slide, reduce);
    if (!document.pictureInPictureElement) return;
    const video = slide.querySelector('video');
    if (slide.hasAttribute('data-locked') || !video) {
      document.exitPictureInPicture().catch(() => {});
      return;
    }
    const previous = document.pictureInPictureElement;
    this.#setCurrent(slide.dataset.episode);
    video.preload = 'auto';
    this.#play(video);
    // The pop-out window can only switch once the next video has loaded its first details.
    const enter = () => {
      video.requestPictureInPicture()
        .then(() => { if (previous && previous !== video) previous.pause(); })
        .catch(() => {});
    };
    if (video.readyState >= 1) enter();
    else video.addEventListener('loadedmetadata', enter, { once: true });
  }

  #setCurrent(number) {
    if (number !== this.#currentEpisode) this.#stopUpNext();
    this.#currentEpisode = number;
    // <s-watch-history> listens for this to power "Continue watching".
    this.dispatchEvent(new CustomEvent('reel:episode', { bubbles: true, detail: { episode: number } }));
    this.querySelectorAll('[data-episode-button]').forEach((button) => {
      if (button.dataset.episode === number) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#upNextTimer);
    this.#observer?.disconnect();
  }
}

if (!customElements.get('s-reel')) customElements.define('s-reel', SReel);
